import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Check,
  Copy,
  Cpu,
  Download,
  Package,
  Terminal,
} from "lucide-react";
import { Button, ButtonLink, Card, copyText } from "@/components/play";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { SitePage } from "@/components/SitePage";

/** A command or prompt with a Copy button. Copied shows only once the clipboard has it; a failure says to select it. */
function CodeBlock({ code, label = "" }: { code: string; label?: string }) {
  const [copied, setCopied] = useState<"idle" | "copied" | "failed">("idle");

  useEffect(() => {
    if (copied === "idle") return;
    const timer = window.setTimeout(() => setCopied("idle"), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  async function handleCopy() {
    setCopied((await copyText(code)) ? "copied" : "failed");
  }

  return (
    <div className="overflow-hidden rounded-lg bg-neutral-1">
      <div className="flex items-center justify-between border-b border-neutral-4 px-4 py-2">
        <span className="text-size-xs font-medium text-neutral-7">{label}</span>
        <Button variant="ghost" size="compact" onClick={handleCopy} aria-label={label === "prompt" ? "Copy prompt" : "Copy command"}>
          {copied === "copied" ? <Check className="text-success" aria-hidden="true" /> : <Copy aria-hidden="true" />}
          {copied === "copied" ? "Copied" : "Copy"}
        </Button>
        <span className="sr-only" role="status">
          {copied === "copied" ? "Copied" : copied === "failed" ? "Couldn’t copy. Select the text instead." : ""}
        </span>
      </div>
      <pre className="overflow-x-auto whitespace-pre-wrap break-words px-4 py-5 font-mono text-sm text-neutral-10">
        <code>{code}</code>
      </pre>
    </div>
  );
}

/** A setup step's title, as a heading in the page outline. */
function StepTitle({ children }: { children: string }) {
  return <h3 className="text-2xl font-black text-neutral-10">{children}</h3>;
}

/** Opens in a new tab: said to assistive technology after the visible label. */
const newTab = <span className="sr-only"> (opens in a new tab)</span>;

function getApiOrigin() {
  const apiBase = import.meta.env.VITE_API_BASE_URL || "http://localhost:3000/api/v1";
  return apiBase.replace(/\/api\/v1\/?$/, "");
}

const API_ORIGIN = getApiOrigin();
const MCP_URL = `${API_ORIGIN}/mcp`;
const SKILL_DOWNLOAD_URL = `${API_ORIGIN}/downloads/streambridge-skill.zip`;
const SKILL_REPO_URL = "https://github.com/livelabs-ventures/streambridge-skill";
const API_REFERENCE_URL = `${SKILL_REPO_URL}/blob/main/references/API.md`;

const quickstarts = [
  {
    title: "Trail run",
    prompt: "Create Ridge Run with start and finish cameras. Return the watch link and camera invitations.",
    note: "A public event and camera slots. Connect the cameras to send video.",
  },
  {
    title: "Cycling",
    prompt: "Create a cycling event with a lead moto and finish camera. Keep the moto private in the workspace and OBS.",
    note: "Choose which cameras appear on the public event page.",
  },
  {
    title: "Athletes",
    prompt: "Add my start list to Ridge Run and connect LNX tracking. Give me the webhook URL for LNX.",
    note: "Your roster joins the public event page. Each athlete appears on the map once their tracker reports.",
  },
  {
    title: "Live show",
    prompt: "Prepare a live show for my existing event and open the OBS setup.",
    note: "An optional show built from the event’s camera sources.",
  },
];

const mcpCommands = {
  "General operator tools": "bash scripts/streambridge mcp tools",
  "Host pilot tools": "bash scripts/streambridge mcp --profile host tools",
  "Host workspace": "bash scripts/streambridge mcp --profile host call get_workspace_context '{}'",
} as const;

export function McpDocsPage() {
  useDocumentTitle("CLI, MCP & skills · StreamBridge");
  return (
    <SitePage mainId="docs-main">
      <section className="relative overflow-hidden px-6 py-20 md:py-24">
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-8 lg:grid-cols-[0.72fr_1.28fr] lg:items-start">
            <div className="space-y-6">
              <div className="inline-flex items-center justify-center rounded-full bg-neutral-2 p-4" aria-hidden="true">
                <Terminal className="h-6 w-6 text-neutral-10" />
              </div>

              <div className="space-y-4">
                <p className="text-size-xs font-medium text-neutral-7">Docs / CLI, MCP & skills</p>
                <h1 className="text-5xl font-black text-neutral-10 md:text-6xl">
                  AI
                  <br />
                  Producer
                </h1>
                <p className="max-w-xs text-xl leading-10 text-neutral-7">
                  Describe the event. Invite the cameras. Go live.
                </p>
                <p className="max-w-sm text-base text-neutral-7">
                  Use the official skill to set up and manage endurance events from your agent.
                </p>
                <p className="max-w-sm text-sm text-neutral-7">
                  Create an event, invite your cameras, and share its public watch page. Add athletes and a live show when you want them.
                </p>
              </div>

              <div className="flex max-w-xs flex-col gap-3">
                <ButtonLink href={SKILL_REPO_URL} target="_blank" rel="noreferrer">
                  <Package aria-hidden="true" />
                  Install the skill
                  {newTab}
                </ButtonLink>
                <ButtonLink href={SKILL_DOWNLOAD_URL} variant="secondary">
                  <Download aria-hidden="true" />
                  Download skill zip
                </ButtonLink>
                <ButtonLink href={API_REFERENCE_URL} target="_blank" rel="noreferrer" variant="secondary">
                  <Terminal aria-hidden="true" />
                  API reference
                  {newTab}
                </ButtonLink>
                <ButtonLink href="#mcp" variant="ghost">
                  <Cpu aria-hidden="true" />
                  MCP tools
                </ButtonLink>
              </div>
            </div>

            <Card title="Skill-first setup">
              <div className="space-y-8">
                <div className="space-y-2">
                  <StepTitle>1. Create a PAT</StepTitle>
                  <p className="text-base text-neutral-7">
                    Go to <Link to="/settings" className="text-neutral-10 underline underline-offset-4 hover:text-neutral-8">Settings &gt; API tokens</Link> and create a token.
                  </p>
                </div>

                <div className="space-y-3">
                  <StepTitle>2. Install the skill</StepTitle>
                  <CodeBlock
                    label="bash"
                    code={`npx skills add ${SKILL_REPO_URL} --skill streambridge`}
                  />
                </div>

                <div className="space-y-3">
                  <StepTitle>3. Use the bundled bash CLI</StepTitle>
                  <CodeBlock
                    label="bash"
                    code={`bash scripts/streambridge doctor\nbash scripts/streambridge events list`}
                  />
                  <p className="text-sm text-neutral-7">
                    The download includes a Bash CLI and API reference. MCP and Python helpers require Python 3.10+.
                  </p>
                </div>

                <div className="space-y-3">
                  <StepTitle>4. Ask for the setup you want</StepTitle>
                  <CodeBlock
                    label="prompt"
                    code={`Create Ridge Run with start and finish cameras. Return the watch link and camera invitations.`}
                  />
                  <p className="text-sm text-neutral-7">
                    On first run, the skill asks for your token and saves it locally in <code className="rounded bg-neutral-3 px-1.5 py-0.5 font-mono text-neutral-10 [overflow-wrap:anywhere]">~/.streambridge/auth.json</code>.
                  </p>
                  <p className="text-sm text-neutral-7">
                    Your event has a public map, featured stream, and chat page immediately. Connect the cameras to send video. Custom page build and publish are optional and require Director access.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="mx-auto max-w-6xl space-y-12">
          <div className="space-y-5">
            <div>
              <p className="text-size-xs font-medium text-neutral-7">
                Quickstart prompts
              </p>
              <h2 className="mt-2 text-3xl font-black text-neutral-10">
                Start with one sentence.
              </h2>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {quickstarts.map((item) => (
                <Card key={item.title} title={item.title} headingLevel={3}>
                  <CodeBlock label="prompt" code={item.prompt} />
                  <p className="mt-4 text-sm text-neutral-7">
                    {item.note}
                  </p>
                </Card>
              ))}
            </div>
          </div>

          <Card
            id="mcp"
            as="section"
            title="Prefer MCP transport?"
            description="The general operator endpoint uses your PAT. The host profile has 11 scoped tools and uses OAuth account linking with a selected organization. The host pilot is disabled by default and requires enabled access."
            icon={<Cpu />}
          >
            <p className="text-sm text-neutral-7">
              General endpoint: <code className="rounded bg-neutral-3 px-1.5 py-0.5 font-mono text-neutral-10 [overflow-wrap:anywhere]">{MCP_URL}</code>. Host endpoint: <code className="rounded bg-neutral-3 px-1.5 py-0.5 font-mono text-neutral-10 [overflow-wrap:anywhere]">{MCP_URL}/chatgpt</code>, which the{" "}
              <Link to="/docs/chatgpt" className="text-neutral-10 underline underline-offset-4 hover:text-neutral-8">ChatGPT plugin</Link> connects to.
              For CLI testing, the host profile reads an issued grant from <code className="rounded bg-neutral-3 px-1.5 py-0.5 font-mono text-neutral-10 [overflow-wrap:anywhere]">STREAMBRIDGE_HOST_TOKEN</code>.
              PATs authorize the general profile, which reaches your organization’s events and the events you manage for another organization. Invitation and OBS host tools open StreamBridge setup pages.
            </p>

            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {Object.entries(mcpCommands).map(([label, command]) => (
                <div key={label}>
                  <h3 className="text-sm font-medium text-neutral-10">{label}</h3>
                  <div className="mt-3">
                    <CodeBlock label="bash" code={command} />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </section>
    </SitePage>
  );
}

export default McpDocsPage;
